import { PayloadAction } from '@reduxjs/toolkit';
import { AxiosResponse, isAxiosError } from 'axios';
import { all, call, put, takeEvery, takeLatest } from 'redux-saga/effects';

import { httpDELETE, httpGET, httpPOST, httpPUT } from '../../services/http.ts';
import { CreateFeatureFlagRequest, FeatureFlag, UpdateFeatureFlagRequest } from '../../types/featureFlags.ts';
import { featureFlagsReducer } from '../reducers/featureFlags.reducer.ts';
import { toastReducer } from '../reducers/toast.reducer.ts';

const {
  loadFeatureFlags,
  setFeatureFlags,
  setFeatureFlagsError,
  createFeatureFlag,
  setCreateFeatureFlagLoading,
  setCreateFeatureFlagSuccess,
  setCreateFeatureFlagError,
  updateFeatureFlag,
  setUpdateFeatureFlagLoading,
  setUpdateFeatureFlagSuccess,
  setUpdateFeatureFlagError,
  deleteFeatureFlag,
  setDeleteFeatureFlagLoading,
  setDeleteFeatureFlagSuccess,
  setDeleteFeatureFlagError,
  loadUserFeatureFlags,
  setUserFeatureFlags,
  setUserFeatureFlagsError,
} = featureFlagsReducer.actions;
const { showToast } = toastReducer.actions;

function* loadFeatureFlagsAction() {
  try {
    const { data }: AxiosResponse<FeatureFlag[]> = yield call(httpGET, `/features/all`);
    yield put(setFeatureFlags(data));
  } catch (error) {
    let errorMessage = 'featureFlags.toast.loadFlagsError';
    if (isAxiosError(error) && error.response?.data) {
      errorMessage = error.response.data;
    }
    yield put(setFeatureFlagsError(errorMessage));
    yield put(
      showToast({
        message: errorMessage,
        type: 'error',
      }),
    );
  }
}

function* createFeatureFlagAction(action: PayloadAction<CreateFeatureFlagRequest>) {
  try {
    yield put(setCreateFeatureFlagLoading());

    const { data }: AxiosResponse<FeatureFlag> = yield call(httpPOST, `/features`, action.payload);

    yield put(setCreateFeatureFlagSuccess(data));

    yield put(
      showToast({
        message: 'featureFlags.toast.createSuccess',
        type: 'success',
      }),
    );
  } catch (error) {
    let errorMessage = 'featureFlags.toast.createError';
    if (isAxiosError(error) && error.response?.data) {
      errorMessage = error.response.data;
    }

    yield put(setCreateFeatureFlagError(errorMessage));

    yield put(
      showToast({
        message: errorMessage,
        type: 'error',
      }),
    );
  }
}

function* updateFeatureFlagAction(action: PayloadAction<{ id: number; request: UpdateFeatureFlagRequest }>) {
  try {
    yield put(setUpdateFeatureFlagLoading());

    const { data }: AxiosResponse<FeatureFlag> = yield call(
      httpPUT,
      `/features/${action.payload.id}`,
      action.payload.request,
    );

    yield put(setUpdateFeatureFlagSuccess(data));

    yield put(
      showToast({
        message: 'featureFlags.toast.updateSuccess',
        type: 'success',
      }),
    );
  } catch (error) {
    let errorMessage = 'featureFlags.toast.updateError';
    if (isAxiosError(error) && error.response?.data) {
      errorMessage = error.response.data;
    }

    yield put(setUpdateFeatureFlagError(errorMessage));

    yield put(
      showToast({
        message: errorMessage,
        type: 'error',
      }),
    );
  }
}

function* deleteFeatureFlagAction(action: PayloadAction<number>) {
  try {
    yield put(setDeleteFeatureFlagLoading());

    yield call(httpDELETE, `/features/${action.payload}`, {});

    yield put(setDeleteFeatureFlagSuccess(action.payload));

    yield put(
      showToast({
        message: 'featureFlags.toast.deleteSuccess',
        type: 'success',
      }),
    );
  } catch (error) {
    let errorMessage = 'featureFlags.toast.deleteError';
    if (isAxiosError(error) && error.response?.data) {
      errorMessage = error.response.data;
    }

    yield put(setDeleteFeatureFlagError(errorMessage));

    yield put(
      showToast({
        message: errorMessage,
        type: 'error',
      }),
    );
  }
}

function* loadUserFeatureFlagsAction() {
  try {
    const { data }: AxiosResponse<string[]> = yield call(httpGET, `/features`);
    yield put(setUserFeatureFlags(data));
  } catch (error) {
    let errorMessage = 'featureFlags.toast.loadUserFlagsError';
    if (isAxiosError(error) && error.response?.data) {
      errorMessage = error.response.data;
    }
    yield put(setUserFeatureFlagsError(errorMessage));
  }
}

export default function* featureFlagsSaga() {
  yield all([
    takeLatest(loadFeatureFlags.type, loadFeatureFlagsAction),
    takeEvery(createFeatureFlag.type, createFeatureFlagAction),
    takeEvery(updateFeatureFlag.type, updateFeatureFlagAction),
    takeEvery(deleteFeatureFlag.type, deleteFeatureFlagAction),
    takeLatest(loadUserFeatureFlags.type, loadUserFeatureFlagsAction),
  ]);
}
